/**
 * Sales agents are a database Role named `sales_agent`, not one of the `UserRole` enums,
 * so agent-only features are gated on the privileges/permissions carried by the user's
 * active roles rather than on the role name itself.
 */
export const SALES_AGENT_ROLE_NAME = "sales_agent";

export const AGENT_PRIVILEGES = {
  EMAIL_LOGIN: "email_login",
  SALES_AGENT_PORTAL: "sales_agent_portal",
} as const;

export const AGENT_PERMISSIONS = {
  LOCATION_DISTANCE: "vehicle:location-distance",
  DETAILED_WARRANTY: "vehicle:detailed-warranty",
} as const;

export type AgentAccess = {
  isSalesAgent: boolean;
  hasSalesAgentPortal: boolean;
  canViewLocationDistance: boolean;
  canViewDetailedWarranty: boolean;
};

export const EMPTY_AGENT_ACCESS: AgentAccess = {
  isSalesAgent: false,
  hasSalesAgentPortal: false,
  canViewLocationDistance: false,
  canViewDetailedWarranty: false,
};

type RoleLike = {
  name?: string;
  isActive?: boolean;
  permissions?: string[] | null;
  privileges?: string[] | null;
  permissionsMap?: Record<string, string[] | string | boolean | null | undefined> | null;
};

type UserWithRoles = {
  roles?: RoleLike[] | null;
};

/**
 * Derives the agent-facing flags from a user's roles. Only the derived booleans are kept
 * (and later stored on the session) so the auth cookie never carries full permission lists.
 */
export const collectAgentAccess = (user?: UserWithRoles | null): AgentAccess => {
  const activeRoles = (user?.roles ?? []).filter((role) => role && role.isActive !== false);

  const privileges = new Set(activeRoles.flatMap((role) => role.privileges ?? []));
  const permissions = new Set(
    activeRoles.flatMap((role) => {
      const mappedPermissions = Object.entries(role.permissionsMap ?? {}).flatMap(([resource, values]) => {
        if (values === true) return [resource];
        if (!Array.isArray(values)) return [];
        return [resource, ...values.flatMap((value) => [value, `${resource}:${value}`])];
      });

      return [...(role.permissions ?? []), ...mappedPermissions];
    }),
  );

  return {
    isSalesAgent: activeRoles.some((role) => role.name === SALES_AGENT_ROLE_NAME),
    hasSalesAgentPortal: privileges.has(AGENT_PRIVILEGES.SALES_AGENT_PORTAL),
    canViewLocationDistance: permissions.has(AGENT_PERMISSIONS.LOCATION_DISTANCE),
    canViewDetailedWarranty: permissions.has(AGENT_PERMISSIONS.DETAILED_WARRANTY),
  };
};
